import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Box,
	Chip,
	ChipProps,
	Stack,
	Tooltip,
	Typography,
	alpha,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import { IChecklistOpenTask } from "@/components/Procedures/Checklist/utils/Types";

import { FeedbackCard } from "@/helpers/components/CommentSuggestion/FeedbackCard";

import { UserContext } from "@/model/User/UserContext";

import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);
dayjs.extend(relativeTime);

type StatusKey = "Verified" | "Declined" | "Pending" | string;

const statusIcon = (status: StatusKey) => {
	switch (status) {
		case "Verified":
			return <CheckCircleIcon fontSize="small" />;
		case "Declined":
			return <ErrorIcon fontSize="small" />;
		default:
			return <HourglassEmptyIcon fontSize="small" />;
	}
};

const statusColor = (status: StatusKey): ChipProps["color"] => {
	switch (status) {
		case "Verified":
			return "success";
		case "Declined":
			return "error";
		default:
			return "info";
	}
};

const clampStyles = (lines: number) => ({
	display: "-webkit-box",
	WebkitLineClamp: lines,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
});

export const ProcedureUpdateSuggestion: FC<IChecklistOpenTask> = (props) => {
	const { suggestion, isOnlySuggestions } = props;

	const [isExpanded, setIsExpanded] = useState(false);
	const { timezone } = useTime();
	const user = useContext(UserContext);

	// read from /procedure/:id/checklist/:schemeId
	const { id: procIdParam, schemeId: schemeIdParam } = useParams<RouteParams>();
	const procedureId = procIdParam ? Number(procIdParam) : undefined;
	const checklistId = schemeIdParam ? Number(schemeIdParam) : undefined;

	const collapsedLines = isOnlySuggestions ? 8 : 4;

	const createdAt = useMemo(
		() => dayjs.utc(suggestion.Created).tz(timezone),
		[suggestion.Created, timezone],
	);

	const exactTime = useMemo(
		() => createdAt.format("DD/MM/YY, HH:mm:ss"),
		[createdAt],
	);

	const relative = useMemo(() => createdAt.fromNow(), [createdAt]);

	const toggleExpand = useCallback(() => setIsExpanded((v) => !v), []);

	const attachmentUrl = suggestion.Attachment?.Url?.replace("//", "/");

	// Infer which ProcedureUpdates tab to use
	const inferredTab = useMemo<
		"newImprov" | "myImprov" | "verified" | "declined"
	>(() => {
		const st = String(suggestion.Status || "").toLowerCase();
		if (st === "verified") return "verified";
		if (st === "declined") return "declined";
		if (user?.Id && suggestion?.CreatedBy?.Id === user.Id) return "myImprov";
		return "newImprov";
	}, [suggestion.Status, suggestion?.CreatedBy?.Id, user?.Id]);

	// Build deep link ONLY when both ids exist
	const deepLink = useMemo(() => {
		if (!procedureId || !checklistId) return undefined;
		const qs = new URLSearchParams();
		qs.set("id", String(procedureId));
		qs.set("checklistId", String(checklistId));
		if (suggestion?.Id) qs.set("suggestionId", String(suggestion.Id));
		if (suggestion?.StepId) qs.set("stepId", String(suggestion.StepId));
		return `/procedures/procedureUpdates/${inferredTab}?${qs.toString()}`;
	}, [
		procedureId,
		checklistId,
		inferredTab,
		suggestion?.Id,
		suggestion?.StepId,
	]);

	// Seed selection for destination view
	const seedSelection = useCallback(() => {
		if (!procedureId || !checklistId) return;
		try {
			localStorage.setItem(
				`suggestionSelection_${inferredTab}`,
				JSON.stringify({
					id: procedureId,
					checklistId,
					stepId: suggestion?.StepId,
					suggestionId: suggestion?.Id,
				}),
			);
		} catch {
			/* ignore */
		}
	}, [
		procedureId,
		checklistId,
		inferredTab,
		suggestion?.Id,
		suggestion?.StepId,
	]);

	return (
		<FeedbackCard
			suggestion={suggestion}
			title={
				<Translate String="Procedure.Checklist.Step.Suggestion.Suggestion" />
			}
			isExpanded={isExpanded}
			toggleExpand={toggleExpand}
			chip={
				<Stack direction="row" spacing={1} alignItems="center">
					<Tooltip title={exactTime} arrow>
						<Chip
							size="small"
							variant="outlined"
							label={relative}
							sx={{ borderRadius: 1.5, "& .MuiChip-label": { px: 0.75 } }}
						/>
					</Tooltip>

					{deepLink && (
						<Tooltip
							title="Open this suggestion in Procedure Updates (new tab)"
							arrow
						>
							<Chip
								size="small"
								clickable
								component={RouterLink as any}
								to={deepLink}
								target="_blank"
								rel="noopener noreferrer"
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();
									seedSelection();
								}}
								icon={<OpenInNewIcon fontSize="small" />}
								label="Open"
								sx={{
									borderRadius: 1.5,
									"& .MuiChip-label": { px: 0.75, fontWeight: 700 },
								}}
							/>
						</Tooltip>
					)}
				</Stack>
			}
		>
			<Stack spacing={1.25} sx={{ width: "100%" }}>
				{/* Content */}
				<Box sx={isExpanded ? undefined : clampStyles(collapsedLines)}>
					<SATOSContentRender content={suggestion.Description} />
				</Box>

				{/* Optional attachment preview */}
				{attachmentUrl && (
					<Box
						component="a"
						href={attachmentUrl}
						target="_blank"
						rel="noopener noreferrer"
						sx={(t) => ({
							display: "inline-block",
							borderRadius: 2,
							overflow: "hidden",
							border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
							transition: "transform .2s ease, box-shadow .2s ease",
							"&:hover": {
								transform: "translateY(-2px)",
								boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
							},
							maxWidth: 320,
						})}
					>
						<Box
							component="img"
							src={attachmentUrl}
							alt="Attachment"
							sx={{ display: "block", width: "100%", height: "auto" }}
						/>
					</Box>
				)}

				{/* Footer */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Tooltip title={exactTime} arrow>
						<Typography variant="body2" color="text.secondary">
							{exactTime}
						</Typography>
					</Tooltip>

					<Chip
						size="small"
						icon={statusIcon(suggestion.Status as StatusKey)}
						label={
							<Translate String={suggestion.Status}>
								{suggestion.Status}
							</Translate>
						}
						color={statusColor(suggestion.Status as StatusKey)}
						sx={{
							borderRadius: 1.5,
							bgcolor: (theme) =>
								alpha(
									(
										theme.palette[
											statusColor(suggestion.Status as StatusKey)
										] ?? theme.palette.info
									).main,
									0.08,
								),
							color: (theme) =>
								(
									theme.palette[statusColor(suggestion.Status as StatusKey)] ??
									theme.palette.info
								).main,
							"& .MuiChip-icon": { color: "inherit" },
							border: "none",
							fontWeight: 500,
						}}
					/>
				</Box>
			</Stack>
		</FeedbackCard>
	);
};

export default ProcedureUpdateSuggestion;
